import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ReactionListItem } from '../../types/reactions';
import ReactionCard from './ReactionCard';
import apiClient from '../../api/client';

interface ProjectReactionsTabProps {
  projectId: number;
}

const ProjectReactionsTab: React.FC<ProjectReactionsTabProps> = ({ projectId }) => {
  const navigate = useNavigate();
  const [reactions, setReactions] = useState<ReactionListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchReactions = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await apiClient.get(`/projects/${projectId}/reactions?limit=200`);
        if (!cancelled) {
          const data = res.data;
          const items = Array.isArray(data) ? data : (data?.items || data?.reactions || data?.data || []);
          setReactions(items);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || err?.message || 'Failed to load reactions');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchReactions();
    return () => { cancelled = true; };
  }, [projectId]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4" />
        <span className="text-gray-500 dark:text-gray-400 font-medium">Loading reactions...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-red-500 bg-red-50 px-6 py-4 rounded-xl font-medium">{error}</div>
      </div>
    );
  }

  if (reactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-24 h-24 mb-6 bg-gradient-to-br from-emerald-100 to-teal-50 rounded-full flex items-center justify-center">
          <svg className="w-12 h-12 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        </div>
        <p className="text-xl font-semibold text-gray-700 dark:text-gray-300">No reactions yet</p>
        <p className="text-gray-400 mt-2 text-center max-w-md">
          Upload RXN or RDF files, or import reactions from your data files to see them here.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto w-full">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          <span className="text-2xl font-bold text-gray-800 dark:text-gray-200">{reactions.length}</span>
          <span className="ml-1">reaction{reactions.length !== 1 ? 's' : ''}</span>
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {reactions.map((reaction) => (
          <ReactionCard
            key={reaction.id}
            reaction={reaction}
            onClick={(id) => navigate(`/chemistry/reactions/${id}`)}
          />
        ))}
      </div>
    </div>
  );
};

export default ProjectReactionsTab;
